import { API_RESPONSE_PROTOCOL_VERSION } from "../api-response-cache-kinds";
import { dataCacheKey } from "./data-cache-identity";
import {
  TABLE_RESPONSE_CACHE_DB, TABLE_RESPONSE_CACHE_IO_TIMEOUT_MS, TABLE_RESPONSE_CACHE_OPEN_TIMEOUT_MS,
  TABLE_RESPONSE_CACHE_WRITE_TIMEOUT_MS,
  canPersistTableResponse, usableTableResponse, tableResponseEvictions,
  type TableResponseMetadata,
} from "./table-response-cache-policy";

const META = "metadata";
const BODIES = "bodies";
const CONTROL = "control";
const EPOCH = "epoch";
const STORES = [META, BODIES, CONTROL];
let connection: Promise<IDBDatabase | null> | undefined;
const backgroundWrites = new Set<IDBTransaction>();
let writeGeneration = 0;

function guarded(tx: IDBTransaction, action: () => void): () => void {
  return () => { try { action(); } catch { try { tx.abort(); } catch {} } };
}

export type TableResponseSnapshot = {
  key: string;
  identity: string;
  epoch: string | number;
  requestedAt: number;
  metadata?: TableResponseMetadata;
  body?: Blob;
};

/** IndexedDB — необязательное ускорение. blocked/ошибка/зависание не задерживают сеть. */
function openCache(): Promise<IDBDatabase | null> {
  if (connection) return connection;
  connection = new Promise(resolve => {
    let settled = false;
    const finish = (db: IDBDatabase | null) => {
      if (settled) { db?.close(); return; }
      settled = true;
      clearTimeout(timer);
      resolve(db);
    };
    const timer = setTimeout(() => finish(null), TABLE_RESPONSE_CACHE_OPEN_TIMEOUT_MS);
    try {
      if (typeof indexedDB === "undefined") { finish(null); return; }
      const request = indexedDB.open(TABLE_RESPONSE_CACHE_DB, 1);
      request.onupgradeneeded = () => {
        if (settled) { request.transaction?.abort(); return; }
        request.result.createObjectStore(META, { keyPath: "key" });
        request.result.createObjectStore(BODIES);
        // Новая БД получает новую эпоху: удаление/пересоздание не должно
        // разрешить запись ответа, начатого ещё до удаления.
        request.result.createObjectStore(CONTROL).put(crypto.randomUUID(), EPOCH);
      };
      request.onerror = request.onblocked = () => finish(null);
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => { db.close(); connection = undefined; };
        db.onclose = () => { connection = undefined; };
        finish(db);
      };
    } catch { finish(null); }
  });
  return connection;
}

/** Завершаем по commit, не по success отдельного запроса. Ошибки — cache miss. */
async function transaction<T>(mode: IDBTransactionMode, fallback: T, run: (tx: IDBTransaction, result: (value: T) => void) => void, kind: "read" | "write" | "clear" = "read"): Promise<T> {
  const background = kind === "write";
  const generation = writeGeneration;
  const db = await openCache();
  if (!db || (background && generation !== writeGeneration)) return fallback;
  return new Promise(resolve => {
    let tx: IDBTransaction | undefined;
    let value = fallback;
    const finish = (result: T) => {
      clearTimeout(timer);
      if (tx) backgroundWrites.delete(tx);
      resolve(result);
    };
    const timer = setTimeout(() => { try { tx?.abort(); } catch {} finish(fallback); },
      kind === "read" ? TABLE_RESPONSE_CACHE_IO_TIMEOUT_MS : TABLE_RESPONSE_CACHE_WRITE_TIMEOUT_MS);
    try {
      tx = db.transaction(STORES, mode);
      if (background) backgroundWrites.add(tx);
      tx.oncomplete = () => finish(value);
      tx.onabort = tx.onerror = () => finish(fallback);
      run(tx, next => { value = next; });
    } catch {
      try { tx?.abort(); } catch {}
      finish(fallback);
    }
  });
}

export async function readTableResponse(url: string, identity: string, includeBody: boolean): Promise<TableResponseSnapshot | null> {
  if (!canPersistTableResponse(url, identity)) return null;
  const key = dataCacheKey(url, identity);
  const requestedAt = Date.now();
  return transaction<TableResponseSnapshot | null>("readonly", null, (tx, result) => {
    const snapshot: TableResponseSnapshot = { key, identity, requestedAt, epoch: 0 };
    const epoch = tx.objectStore(CONTROL).get(EPOCH);
    epoch.onsuccess = () => { snapshot.epoch = epoch.result ?? 0; };
    result(snapshot);
    if (!includeBody) return;
    const meta = tx.objectStore(META).get(key);
    meta.onsuccess = guarded(tx, () => {
      const entry: TableResponseMetadata = meta.result;
      if (!entry || entry.key !== key || entry.identity !== identity || !usableTableResponse(entry, requestedAt)) return;
      const body = tx.objectStore(BODIES).get(key);
      body.onsuccess = () => {
        if (body.result instanceof Blob && body.result.size === entry.bytes) {
          snapshot.metadata = entry;
          snapshot.body = body.result;
        }
      };
    });
  });
}

/** Blob приходит из HTTP-ответа: не stringify огромного объекта и не его копия. */
export async function saveTableResponse(snapshot: TableResponseSnapshot, etag: string, body: Blob): Promise<boolean> {
  const now = Date.now();
  const incoming: TableResponseMetadata = {
    key: snapshot.key, identity: snapshot.identity, etag, bytes: body.size,
    protocol: API_RESPONSE_PROTOCOL_VERSION, savedAt: now, usedAt: now, requestedAt: snapshot.requestedAt,
  };
  if (!usableTableResponse(incoming, now)) return false;
  return transaction("readwrite", false, (tx, result) => {
    const epoch = tx.objectStore(CONTROL).get(EPOCH);
    epoch.onsuccess = guarded(tx, () => {
      if ((epoch.result ?? 0) !== snapshot.epoch) return;
      const meta = tx.objectStore(META);
      const bodies = tx.objectStore(BODIES);
      const request = meta.getAll();
      request.onsuccess = guarded(tx, () => {
        const entries: TableResponseMetadata[] = request.result;
        // Поздний ответ другой вкладки не затирает более новый запрос.
        if (entries.some(entry => entry.key === incoming.key && entry.requestedAt > incoming.requestedAt)) return;
        for (const key of tableResponseEvictions(entries, incoming, now)) { meta.delete(key); bodies.delete(key); }
        bodies.put(body, incoming.key);
        meta.put(incoming);
        result(true);
      });
    });
  }, "write");
}

/** 304 обновляет только маленькую метку использования, не переписывает Blob. */
export async function touchTableResponse(snapshot: TableResponseSnapshot, etag: string): Promise<void> {
  await transaction("readwrite", undefined, tx => {
    const epoch = tx.objectStore(CONTROL).get(EPOCH);
    epoch.onsuccess = guarded(tx, () => {
      if ((epoch.result ?? 0) !== snapshot.epoch) return;
      const store = tx.objectStore(META);
      const request = store.get(snapshot.key);
      request.onsuccess = guarded(tx, () => {
        const entry: TableResponseMetadata | undefined = request.result;
        if (entry?.etag === etag && usableTableResponse(entry, Date.now())) store.put({ ...entry, usedAt: Date.now() });
      });
    });
  }, "write");
}

/** Собственная БД, не чужие черновики. Epoch блокирует поздние записи ВСЕХ вкладок. */
export async function clearTableResponses(identity?: string): Promise<void> {
  // Не ждём фоновые записи до 10 с при выходе. Отменяем также записи,
  // которые ещё ждут openCache; межвкладочную защиту по-прежнему даёт EPOCH.
  writeGeneration++;
  for (const tx of backgroundWrites) { try { tx.abort(); } catch {} }
  backgroundWrites.clear();
  const clearing = transaction("readwrite", undefined, tx => {
    const control = tx.objectStore(CONTROL);
    control.put(crypto.randomUUID(), EPOCH);
    const meta = tx.objectStore(META);
    const bodies = tx.objectStore(BODIES);
    if (!identity) { meta.clear(); bodies.clear(); return; }
    const request = meta.getAll();
    request.onsuccess = guarded(tx, () => {
      for (const entry of request.result as TableResponseMetadata[]) {
        if (entry.identity === identity) { meta.delete(entry.key); bodies.delete(entry.key); }
      }
    });
  }, "clear");
  // Другая вкладка может ещё держать write-lock. Выход/401 не ждут его,
  // но очистку НЕ abort-им через 250 мс: она должна сменить EPOCH и удалить
  // тела после освобождения lock. Более поздние записи встанут за ней.
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([clearing, new Promise<void>(resolve => {
      timer = setTimeout(resolve, TABLE_RESPONSE_CACHE_IO_TIMEOUT_MS);
    })]);
  } finally { clearTimeout(timer); }
}
